import React, { useState } from 'react';
import { InlineValueEditor } from './InlineValueEditor';

interface AutoCorrectedValue {
  testName: string;
  date: string;
  originalValue: number;
  originalUnits: string;
  correctedValue: number;
  correctedUnits: string;
  reason: string;
}

interface AutoCorrectionManagerProps {
  corrections: AutoCorrectedValue[];
  onAcceptAll: () => void;
  onRevertCorrection: (testName: string, date: string) => void;
  onModifyCorrection: (testName: string, date: string, newValue: number, newUnits: string) => void;
  onClose: () => void;
}

export const AutoCorrectionManager: React.FC<AutoCorrectionManagerProps> = ({
  corrections,
  onAcceptAll,
  onRevertCorrection,
  onModifyCorrection,
  onClose
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  if (corrections.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Review Auto-Corrections
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {corrections.length} value{corrections.length > 1 ? 's were' : ' was'} automatically corrected
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Corrections List */}
        <div className="px-6 py-4 overflow-y-auto max-h-[50vh]">
          <div className="space-y-4">
            {corrections.map((correction, index) => (
              <div key={`${correction.testName}-${correction.date}`} 
                   className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{correction.testName}</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(correction.date).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Original:</span>
                      <span className="font-mono bg-red-100 text-red-700 px-2 py-1 rounded">
                        {correction.originalValue} {correction.originalUnits}
                      </span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      <span className="text-gray-600">Corrected to:</span>
                      {editingIndex === index ? (
                        <InlineValueEditor
                          value={correction.correctedValue}
                          units={correction.correctedUnits}
                          testName={correction.testName}
                          date={correction.date}
                          position={{ x: 0, y: 0 }}
                          onSave={(newValue, newUnits) => {
                            onModifyCorrection(correction.testName, correction.date, newValue, newUnits || correction.correctedUnits);
                            setEditingIndex(null);
                          }}
                          onCancel={() => setEditingIndex(null)}
                        />
                      ) : (
                        <button
                          onClick={() => setEditingIndex(index)}
                          className="font-mono bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 transition-colors"
                        >
                          {correction.correctedValue} {correction.correctedUnits}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2 italic">{correction.reason}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => onRevertCorrection(correction.testName, correction.date)}
                      className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Revert
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Click on corrected values to modify them
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  // Revert all corrections
                  corrections.forEach(c => onRevertCorrection(c.testName, c.date));
                  onClose();
                }}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Revert All
              </button>
              <button
                onClick={() => {
                  onAcceptAll();
                  onClose();
                }}
                className="px-4 py-2 text-sm text-white bg-purple-600 rounded hover:bg-purple-700"
              >
                Accept All Corrections
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};